import { useState } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'

const REASONS = [
  'Inaccurate or outdated information',
  'Spam or fake listing',
  'Inappropriate content',
  'Duplicate listing',
  'Business is permanently closed',
  'Troll or harassment (blog)',
  'Other',
]

export default function ReportModal({ targetId, targetType, targetName, onClose }) {
  const { user } = useAuth()
  const [reason, setReason]   = useState('')
  const [details, setDetails] = useState('')
  const [loading, setLoading] = useState(false)
  const [done, setDone]       = useState(false)
  const [error, setError]     = useState('')

  async function handleSubmit(e) {
    e.preventDefault()
    if (!reason) { setError('Please select a reason.'); return }
    setLoading(true)

    const { error: dbErr } = await supabase.from('reports').insert({
      target_id:   targetId,
      target_type: targetType,   // 'listing' | 'blog_post'
      target_name: targetName,
      reason,
      details,
      reported_by: user?.id || null,
      status: 'open',
    })

    if (dbErr) { setError(dbErr.message); setLoading(false); return }

    // Send email notification via Supabase edge function
    try {
      await supabase.functions.invoke('notify-report', {
        body: { targetName, targetType, reason, details }
      })
    } catch (_) {
      // Email notification failure shouldn't block the report
    }

    setLoading(false)
    setDone(true)
  }

  if (done) return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ maxWidth: 400, textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 14 }}>✅</div>
        <h3 style={{ fontSize: 18, fontWeight: 800, marginBottom: 8 }}>Report submitted</h3>
        <p style={{ fontSize: 13, color: 'var(--gray-500)', lineHeight: 1.6, marginBottom: 20 }}>
          Thanks for keeping the directory clean. Our team will review this and take action if needed.
        </p>
        <button onClick={onClose} className="btn btn-primary" style={{ width: '100%' }}>Done</button>
      </div>
    </div>
  )

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ maxWidth: 440 }}>
        <button className="modal-close" onClick={onClose}>×</button>

        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontFamily: 'var(--mono)', color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>
            Report {targetType === 'listing' ? 'Listing' : 'Post'}
          </div>
          <h3 style={{ fontSize: 18, fontWeight: 800, lineHeight: 1.2 }}>{targetName}</h3>
        </div>

        {error && (
          <div style={{ background: '#fee2e2', color: '#dc2626', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 14 }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="field">
            <label>Reason for reporting *</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {REASONS.map(r => (
                <label key={r} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
                  border: `1.5px solid ${reason === r ? 'var(--blue)' : 'var(--gray-200)'}`,
                  background: reason === r ? '#eff6ff' : '#fff',
                  fontSize: 13, fontWeight: reason === r ? 500 : 400,
                  transition: 'all .15s',
                }}>
                  <input
                    type="radio"
                    name="reason"
                    value={r}
                    checked={reason === r}
                    onChange={() => setReason(r)}
                    style={{ accentColor: 'var(--blue)' }}
                  />
                  {r}
                </label>
              ))}
            </div>
          </div>

          <div className="field" style={{ marginTop: 12 }}>
            <label>Additional details <span style={{ fontWeight: 400, color: 'var(--gray-400)' }}>(optional)</span></label>
            <textarea
              value={details}
              onChange={e => setDetails(e.target.value)}
              placeholder="Any extra context that would help our team review this…"
              style={{ minHeight: 80 }}
            />
          </div>

          {!user && (
            <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#92400e', marginBottom: 14 }}>
              You're reporting anonymously. Log in to help us follow up if needed.
            </div>
          )}

          <button type="submit" className="btn btn-danger" style={{ width: '100%' }} disabled={loading}>
            {loading ? 'Submitting…' : '🚩 Submit Report'}
          </button>
        </form>
      </div>
    </div>
  )
}
